import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SectionHeading from "../../components/SectionHeading";
import Pagination from "./../../components/table/Pagination";
import { noDataPlaceholder, ssnAccess } from "./../../constants";
import Info from "./../../components/alerts/Info";
import GTM from "../../GTM";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { setSelectedOdsEmployeeId } from "../../slices/employeeClaimLeaveSlice";

function EmployeesTable(props) {
    const dispatch = useDispatch();
    const [users, setUsers] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [
        employeesWithMultipleRelatedOrgs,
        setEmployeesWithMultipleRelatedOrgs,
    ] = useState([]);

    const processEmployees = () => {
        const emps = {};
        const sortedEmployees = [];
        const employeesWithMultipleOrgs = [];

        employees.forEach((user, index) => {
            const groupKey = getEmployeeGroupKey(user, index);
            if (!emps[groupKey]) emps[groupKey] = [];
            const userWithGroupKey = { ...user, __groupKey: groupKey };

            if (emps[groupKey].length >= 1) {
                handleMultipleOrgs(
                    userWithGroupKey,
                    emps,
                    employeesWithMultipleOrgs,
                    groupKey
                );
            } else {
                emps[groupKey].push(userWithGroupKey);
                sortedEmployees.push({
                    EEID: groupKey,
                    Name: `${user.LastName} ${user.FirstName}`,
                });
            }
        });
        filterEmptyRecords(emps);
        const u = sortedEmployees.map((sortedEmployee) => {
            const empData = emps[sortedEmployee.EEID];
            return Array.isArray(empData) ? empData : [empData];
        });

        return { u, employeesWithMultipleOrgs };
    };

    const handleMultipleOrgs = (
        user,
        emps,
        employeesWithMultipleOrgs,
        groupKey
    ) => {
        if (user.OpenLeave != null || user.OpenClaim != null) {
            if (user.OrganizationId !== emps[groupKey][0].OrganizationId) {
                if (!employeesWithMultipleOrgs.includes(groupKey)) {
                    employeesWithMultipleOrgs.push(groupKey);
                }
            }

            emps[groupKey].push(user);
        }
    };

    const filterEmptyRecords = (emps) => {
        for (const eid in emps) {
            if (emps[eid].length > 1) {
                const filteredRows = emps[eid].filter(
                    (e) => e.OpenLeave !== null || e.OpenClaim !== null
                );

                if (filteredRows.length > 0) {
                    emps[eid] = filteredRows;
                } else {
                    emps[eid] = emps[eid][0];
                }
            }
        }
    };

    // Usage
    useEffect(() => {
        const { u, employeesWithMultipleOrgs } = processEmployees();

        setUsers(u);
        setEmployeesWithMultipleRelatedOrgs(employeesWithMultipleOrgs);
    }, [employees]);

    const addUniqueIdsToEmployees = (emps) => {
        return emps.map((emp, index) => {
            if (!emp.__displayId) {
                return { ...emp, __displayId: `row-${index}` };
            }
            return emp;
        });
    };

    useEffect(() => {
        const empsWithIds = addUniqueIdsToEmployees(props.filteredEmployees);
        setEmployees(empsWithIds);
    }, [props.filteredEmployees]);

    const [pageNumber, setPageNumber] = useState(0);
    const usersPerPage = props.rowRangeChanged ? props.rowRangeChanged : 5;
    const pagesVisited = pageNumber * usersPerPage;

    const getPageCount = () => {
        return Math.ceil(users.length / usersPerPage);
    };

    const changePage = (selected) => {
        setPageNumber(selected);
    };

    function updateSsnState(ssn, ssnState, displayId) {
        let newSSNState = "TRUNCATED";
        if (ssnState === "NORMAL") newSSNState = "HIDDEN";
        else if (ssnState === "TRUNCATED") newSSNState = "NORMAL";

        if (props.ssnDisplay === ssnAccess.Partial && newSSNState === "NORMAL")
            newSSNState = "HIDDEN";

        const newUsers = [];

        employees.forEach((employee) => {
            if (employee.SSN === ssn && employee.__displayId === displayId)
                newUsers.push({ ...employee, SSNState: newSSNState });
            else newUsers.push({ ...employee });
        });

        setEmployees(newUsers);
    }
    const handelClick = (attribute3) => {
        gtm.Event(
            "searching an employee from an organization",
            "button",
            "click",
            "ssn",
            "search return",
            "",
            attribute3,
            ""
        );
    };

    function customizeSSN(fullSSN, ssnState, displayId) {
        if (props.ssnDisplay === ssnAccess["No Access"]) {
            return <></>;
        }

        if (ssnState === "TRUNCATED") {
            const lastSSNDigits = fullSSN.split("-");

            return (
                <span
                    onClick={() => {
                        handelClick("show_ssn");
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            handelClick("show_ssn");
                        }
                    }}
                    tabIndex={0}
                >
                    <span tabIndex={0}>
                        {lastSSNDigits[lastSSNDigits.length - 1]}
                    </span>

                    <span
                        data-testid="truncatedTestVal"
                        onClick={() =>
                            updateSsnState(fullSSN, ssnState, displayId)
                        }
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                updateSsnState(fullSSN, ssnState, displayId);
                            }
                        }}
                        tabIndex={0}
                    >
                        <i
                            className="fa fa-eye part-eye cursor-pointer"
                            aria-hidden="true"
                        ></i>
                    </span>
                </span>
            );
        } else if (ssnState === "NORMAL") {
            return (
                <span>
                    <span tabIndex={0}>{fullSSN}</span>

                    <span
                        data-testid="normalTestValue"
                        onClick={() =>
                            updateSsnState(fullSSN, ssnState, displayId)
                        }
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                updateSsnState(fullSSN, ssnState, displayId);
                            }
                        }}
                        tabIndex="0"
                    >
                        <i
                            className="fa fa-eye-slash full-eye cursor-pointer"
                            aria-hidden="true"
                        ></i>
                    </span>
                </span>
            );
        } else if (ssnState === "HIDDEN") {
            return (
                <span
                    onClick={() => {
                        handelClick("hide_ssn");
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            handelClick("show_ssn");
                        }
                    }}
                    tabIndex={0}
                >
                    <span tabIndex={0}>***-**-****</span>

                    <span
                        data-testid="hiddenTestValue"
                        onClick={() =>
                            updateSsnState(fullSSN, ssnState, displayId)
                        }
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                updateSsnState(fullSSN, ssnState, displayId);
                            }
                        }}
                        tabIndex="0"
                    >
                        <i
                            className="fa fa-eye mask-eye cursor-pointer"
                            aria-hidden="true"
                        ></i>
                    </span>
                </span>
            );
        }
    }

    function customizeRow(item, index) {
        const handleEmployeeSelect = (employeeId) => {
            if (employeeId) {
                dispatch(setSelectedOdsEmployeeId(employeeId));
            }
        };

        const rowKey =
            item?.[0]?.__groupKey ||
            `${item?.[0]?.MasterPartyId || "unknown"}-${item?.[0]?.OrganizationId || "unknown"}-${index}`;

        return (
            <React.Fragment key={rowKey}>
                {item.length !== 1 &&
                    multipleRows(
                        item,
                        index,
                        props,
                        customizeSSN,
                        employeesWithMultipleRelatedOrgs,
                        handleEmployeeSelect
                    )}

                {item.length == 1 &&
                    singleRow(
                        index,
                        props,
                        item,
                        customizeSSN,
                        handleEmployeeSelect
                    )}
            </React.Fragment>
        );
    }

    return (
        <>
            {users.length >= 100 && (
                <Info
                    className="fs-sm mb-20p"
                    message={
                        "The first 100 records matching your search criteria are shown below. If you need to search further, input more characters in your search request."
                    }
                />
            )}

            <div className="section-card px-0">
                <SectionHeading heading="Search Return" className="px-20p" />

                <div className="fixed-header custom-scrollbar">
                    <table className="table-container">
                        <thead>
                            <tr>
                                {props.relatedOrganizationsCount > 0 && (
                                    <th scope="col" tabIndex={0}>
                                        Organization Name
                                    </th>
                                )}
                                <th scope="col" tabIndex={0}>
                                    Name
                                </th>

                                <th scope="col" tabIndex={0}>
                                    EEID
                                </th>

                                {props.ssnDisplay !==
                                    ssnAccess["No Access"] && (
                                    <th scope="col" tabIndex={0}>
                                        SSN
                                    </th>
                                )}

                                <th
                                    scope="col"
                                    className="text-center"
                                    tabIndex={0}
                                >
                                    Open Leave
                                </th>
                                <th
                                    scope="col"
                                    className="text-center"
                                    tabIndex={0}
                                >
                                    Open Claim
                                </th>
                                <th scope="col" tabIndex={0}>
                                    <span className="pl-10p">Coverage</span>
                                </th>
                                <th scope="col" tabIndex={0}>
                                    Leave Reason
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {users
                                .slice(
                                    pagesVisited,
                                    parseInt(pagesVisited) +
                                        parseInt(usersPerPage)
                                )
                                .map((item, index) => {
                                    return customizeRow(
                                        item,
                                        pagesVisited + index
                                    );
                                })}
                        </tbody>
                    </table>
                </div>

                {users.length > 10 && (
                    <div className="pt-20p">
                        <Pagination
                            pageCount={getPageCount()}
                            gotoPage={changePage}
                            setPageSize={props.handleItemsPerPageChange}
                            pageSize={props.rowRangeChanged ?? 10}
                            options={[5, 10, 50, 100]}
                            attribute1="search return"
                            pageName="searching an employee from an organization"
                            attribute4=""
                            tabIndex={0}
                        />
                    </div>
                )}
            </div>
        </>
    );
}

const getEmployeeGroupKey = (user, index) => {
    const employeeId = String(user?.MasterPartyId ?? user?.EEID ?? "").trim();
    const ssn = String(user?.SSN ?? "").trim();
    const orgId = String(user?.OrganizationId ?? "")
        .trim()
        .toLowerCase();

    const keyParts = [employeeId, ssn, orgId].filter(
        (part) => part && part.toLowerCase() !== "not provided"
    );

    if (keyParts.length > 0) {
        return `emp:${keyParts.join("|")}`;
    }

    return `row:${index}`;
};

const getUrlSafeEmployeeId = (user) => {
    const eeid = String(user?.EEID ?? "").trim();
    if (eeid) return eeid;
    return "view";
};

EmployeesTable.propTypes = {
    filteredEmployees: PropTypes.arrayOf(
        PropTypes.shape({
            MasterPartyId: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]).isRequired,
            LastName: PropTypes.string.isRequired,
            FirstName: PropTypes.string.isRequired,
            SSN: PropTypes.string,
            SSNState: PropTypes.oneOf(["NORMAL", "TRUNCATED", "HIDDEN"]),
            OrganizationId: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]).isRequired,
            Organization: PropTypes.string,
            OpenLeave: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]),
            OpenClaim: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]),
            Coverage: PropTypes.string,
            LeaveReason: PropTypes.string,
        })
    ).isRequired,
    ssnDisplay: PropTypes.string.isRequired,
    relatedOrganizationsCount: PropTypes.number,
    selectedOrganization: PropTypes.shape({
        ORGANIZATIONGROUPNAME: PropTypes.string,
    }),
    rowRangeChanged: PropTypes.number,
    handleItemsPerPageChange: PropTypes.func,
};

const conditionalJsxColumn = (dataObject, option) => {
    let returnJsx = noDataPlaceholder;

    if (dataObject) {
        switch (option) {
            case "Leave Reason":
                if (
                    dataObject.OpenLeave !== null &&
                    dataObject.LeaveReason !== null
                )
                    return (
                        <td className="border-bottom-0">
                            <p>{dataObject.LeaveReason} </p>
                        </td>
                    );
                else
                    return (
                        <td className="border-bottom-0">{noDataPlaceholder}</td>
                    );
            case "Leave Reason Value":
                if (dataObject.OpenLeave !== null)
                    return dataObject.LeaveReason;
                break;

            case "Coverage Value":
                if (
                    dataObject.OpenClaim !== null ||
                    dataObject.OpenLeave !== null
                )
                    return dataObject.Coverage;
                break;
            default:
                break;
        }
    }
    return returnJsx;
};

export default React.memo(EmployeesTable);
const gtm = new GTM(null);

function multipleRows(
    item,
    index,
    props,
    customizeSSN,
    employeesWithMultipleRelatedOrgs,
    onEmployeeSelect
) {
    const handleLink = (label, attribute3) => {
        gtm.Event(
            "searching an employee from an organization",
            "link",
            "click",
            label,
            "search return",
            "",
            attribute3,
            ""
        );
    };
    const renderOrganizationName = (user) => {
        if (props.relatedOrganizationsCount > 0) {
            const organizationName = employeesWithMultipleRelatedOrgs.includes(
                user.__groupKey
            )
                ? props.selectedOrganization?.ORGANIZATIONGROUPNAME
                : user.Organization;

            return (
                <td
                    rowSpan={item.length}
                    data-header="Organization Name"
                    tabIndex={0}
                >
                    {organizationName}
                </td>
            );
        }
        return null;
    };

    const renderNameLinks = (user) => {
        const safeId = getUrlSafeEmployeeId(user);
        const navigatingUrl = `/organization/${user.OrganizationId}/employees/${safeId}?ids=${props.relatedIds}`;
        const linkProps = {
            to: navigatingUrl,
            state: {
                ssnState: user.SSNState,
                odsEmployeeHistory: user.__odsEmployeeHistory ?? null,
            },
            onClick: () => {
                handleLink("name", "select_employee");
                onEmployeeSelect(user.MasterPartyId);
            },
        };

        return (
            <>
                <Link {...linkProps}>{`${user.LastName},`}</Link>{" "}
                <Link {...linkProps}>{`${user.FirstName}`}</Link>
            </>
        );
    };

    const renderOpenLeaveLink = (user, handleClick) => {
        if (user.OpenLeave) {
            const safeId = getUrlSafeEmployeeId(user);
            return (
                <Link
                    to={`/organization/${user.OrganizationId}/employees/${safeId}?leave=${user.OpenLeave}&ids=${props.relatedIds}`}
                    state={{
                        ssnState: user.SSNState,
                        odsEmployeeHistory: user.__odsEmployeeHistory ?? null,
                    }}
                    onClick={() => {
                        handleLink("open leave", "select_leave");
                        onEmployeeSelect(user.MasterPartyId);
                    }}
                >
                    {user.OpenLeave}
                </Link>
            );
        } else {
            return <>{noDataPlaceholder}</>;
        }
    };

    const renderOpenClaimLink = (user, handleClick) => {
        if (user.OpenClaim) {
            const safeId = getUrlSafeEmployeeId(user);
            return (
                <Link
                    to={`/organization/${user.OrganizationId}/employees/${safeId}?claim=${user.OpenClaim}&ids=${props.relatedIds}`}
                    state={{
                        ssnState: user.SSNState,
                        odsEmployeeHistory: user.__odsEmployeeHistory ?? null,
                    }}
                    onClick={() => {
                        handleLink("open claim", "select_claim");
                        onEmployeeSelect(user.MasterPartyId);
                    }}
                >
                    {user.OpenClaim}
                </Link>
            );
        } else {
            return <>{noDataPlaceholder}</>;
        }
    };

    const renderLeaveReason = (user, i) => {
        const trimmedLeaveReason = user.LeaveReason?.trim();

        if (!trimmedLeaveReason || user.LeaveReason == null) {
            return (
                <td
                    className={`pt-0 ${
                        i !== item.length - 1 ? "border-bottom-0" : ""
                    }`}
                    tabIndex="0"
                >
                    {noDataPlaceholder}
                </td>
            );
        }
        return (
            <td
                data-header="Leave Reason"
                className={`pt-0 ${
                    i !== item.length - 1 ? "border-bottom-0" : ""
                }`}
                tabIndex="0"
            >
                <span>{user.LeaveReason}</span>
            </td>
        );
    };

    return (
        <>
            {item.map((user, i) => (
                <React.Fragment
                    key={`${user?.__groupKey || "group"}-${user?.OrganizationId || "org"}-${user?.OpenLeave || "leave"}-${user?.OpenClaim || "claim"}-${i}`}
                >
                    {i === 0 && (
                        <tr
                            className={
                                index % 2 === 0
                                    ? "odd vertical-align-top"
                                    : "even vertical-align-top"
                            }
                            data-row-id={user.__displayId}
                        >
                            {renderOrganizationName(user)}
                            <td
                                rowSpan={item.length}
                                data-header="Name"
                                tabIndex={0}
                            >
                                {renderNameLinks(user)}
                            </td>
                            <td
                                rowSpan={item.length}
                                data-header="EEID"
                                tabIndex={0}
                            >
                                {user.EEID ?? noDataPlaceholder}
                            </td>
                            {props.ssnDisplay !== ssnAccess["No Access"] && (
                                <td
                                    rowSpan={item.length}
                                    data-header="SSN"
                                    tabIndex={0}
                                >
                                    {user.SSN
                                        ? customizeSSN(
                                              user.SSN,
                                              user.SSNState,
                                              user.__displayId
                                          )
                                        : "-"}
                                </td>
                            )}
                            <td
                                className="text-center border-left border-bottom-0 md-border-x-0 md-text-left md-d-none"
                                data-header="Open Leave"
                                tabIndex={0}
                            >
                                {renderOpenLeaveLink(user)}
                            </td>
                            <td
                                className="text-center border-left border-right border-bottom-0 md-border-x-0 md-text-left md-d-none"
                                data-header="Open Claim"
                                tabIndex={0}
                            >
                                {renderOpenClaimLink(user)}
                            </td>
                            <td
                                data-header="Coverage"
                                className="border-bottom-0"
                                tabIndex={0}
                            >
                                <span className="pl-10p md-pl-0p">
                                    {user.Coverage ?? noDataPlaceholder}
                                </span>
                            </td>
                            {conditionalJsxColumn(user, "Leave Reason")}
                        </tr>
                    )}

                    {i !== 0 && (
                        <tr
                            className={index % 2 === 0 ? "odd" : "even"}
                            data-row-id={user.__displayId}
                        >
                            <td
                                data-header="Open leave"
                                className={`text-center border-left pt-0 ${
                                    i !== item.length - 1
                                        ? "border-bottom-0"
                                        : ""
                                }`}
                                tabIndex="0"
                            >
                                {renderOpenLeaveLink(user)}
                            </td>
                            <td
                                data-header="Open Claim"
                                className={`text-center border-left border-right pt-0 pr-10p ${
                                    i !== item.length - 1
                                        ? "border-bottom-0"
                                        : ""
                                }`}
                                tabIndex={0}
                            >
                                {renderOpenClaimLink(user)}
                            </td>
                            <td
                                data-header="Coverage"
                                className={`pt-0 ${
                                    i !== item.length - 1
                                        ? "border-bottom-0"
                                        : ""
                                }`}
                                tabIndex="0"
                            >
                                <span className="pl-10p">{user.Coverage}</span>
                            </td>
                            {renderLeaveReason(user, i)}
                        </tr>
                    )}
                </React.Fragment>
            ))}
        </>
    );
}

function singleRow(index, props, item, customizeSSN, onEmployeeSelect) {
    const handleLink = (label, attribute3) => {
        gtm.Event(
            "searching an employee from an organization",
            "link",
            "click",
            label,
            "search return",
            "",
            attribute3,
            ""
        );
    };
    const row = item[0];
    const safeId = getUrlSafeEmployeeId(row);
    const navigatingUrl = `/organization/${row.OrganizationId}/employees/${safeId}?ids=${props.relatedIds}`;

    return (
        <tr
            className={index % 2 == 0 ? "odd" : "even"}
            key={index}
            data-row-id={item[0].__displayId}
        >
            {props.relatedOrganizationsCount > 0 && (
                <td data-header="Organization" tabIndex={0}>
                    {row.Organization}
                </td>
            )}

            <td data-header="Name" tabIndex={0} title="Name">
                <>
                    <Link
                        to={navigatingUrl}
                        state={{
                            ssnState: row.SSNState,
                            odsEmployeeHistory:
                                row.__odsEmployeeHistory ?? null,
                        }}
                        onClick={() => {
                            handleLink("name", "select_employee");
                            onEmployeeSelect(row.MasterPartyId);
                        }}
                    >
                        {row.LastName + ","}
                    </Link>{" "}
                    <Link
                        to={navigatingUrl}
                        state={{
                            ssnState: row.SSNState,
                            odsEmployeeHistory:
                                row.__odsEmployeeHistory ?? null,
                        }}
                        onClick={() => {
                            handleLink("name", "select_employee");
                            onEmployeeSelect(row.MasterPartyId);
                        }}
                    >
                        {row.FirstName}
                    </Link>
                </>
            </td>

            <td data-header="EEID" tabIndex={0} title="EEID">
                {item[0].EEID ?? noDataPlaceholder}
            </td>

            {props.ssnDisplay !== ssnAccess["No Access"] && (
                <td data-header="SSN" tabIndex={0} title="SSN">
                    {row.SSN
                        ? customizeSSN(row.SSN, row.SSNState, row.__displayId)
                        : "-"}
                </td>
            )}

            <td
                data-header="Open Leave"
                className="text-center border-left md-border-x-0 md-text-left"
                tabIndex={0}
                title="Open Leave"
            >
                {row.OpenLeave ? (
                    <Link
                        to={`/organization/${row.OrganizationId}/employees/${safeId}?leave=${row.OpenLeave}&ids=${props.relatedIds}`}
                        state={{
                            ssnState: row.SSNState,
                            odsEmployeeHistory:
                                row.__odsEmployeeHistory ?? null,
                        }}
                        onClick={() => {
                            handleLink("open leave", "select_leave");
                            onEmployeeSelect(item[0].MasterPartyId);
                        }}
                    >
                        {row.OpenLeave}
                    </Link>
                ) : (
                    noDataPlaceholder
                )}
            </td>

            <td
                data-header="Open Claim"
                className="text-center border-left border-right md-border-x-0 md-text-left"
                tabIndex={0}
                title="Open Claim"
            >
                {row.OpenClaim ? (
                    <Link
                        to={`/organization/${row.OrganizationId}/employees/${safeId}?claim=${row.OpenClaim}&ids=${props.relatedIds}`}
                        state={{
                            ssnState: row.SSNState,
                            odsEmployeeHistory:
                                row.__odsEmployeeHistory ?? null,
                        }}
                        onClick={() => {
                            handleLink("open claim", "select_claim");
                            onEmployeeSelect(item[0].MasterPartyId);
                        }}
                    >
                        {row.OpenClaim}
                    </Link>
                ) : (
                    noDataPlaceholder
                )}
            </td>

            <td data-header="Coverage" tabIndex={0} title="Coverage">
                <span className="pl-10p md-pl-0p">
                    {conditionalJsxColumn(row, "Coverage Value")}
                </span>
            </td>

            <td data-header="Leave Reason" tabIndex={0} title="Leave Reason">
                <p className="mb-0">
                    {conditionalJsxColumn(row, "Leave Reason Value")}
                </p>
            </td>
        </tr>
    );
}
